/**
 * Timeline order (#207): the case timeline lists the newest turn first by default, with a per-person toggle between
 * newest first and oldest first. In newest-first mode the composer sits at the top, directly above the newest turn,
 * so nobody scrolls to the bottom of a long ticket to reply. The same order drives the case view, the expanded Riley
 * view, the phone case view and the contractor ticket view.
 *
 * The choice is stored server-side with the person's other preferences (`/me/notifications` `timeline_order`), so it
 * follows them across devices. A browser copy is kept so the first paint already has the right order, and it is the
 * only store where there is no Patchbay user (the contractor portal) or when the API predates the field.
 * Owner: feat/newest-first (#207).
 */
import { Button, cn, safeStorage, Tooltip, TooltipProvider, toast } from '@patchbay/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowDownWideNarrow, ArrowUpNarrowWide, ChevronsDown, ChevronsDownUp, ChevronsUp, ChevronsUpDown } from 'lucide-react'
import * as React from 'react'
import { currentCsrfToken } from '../../lib/api/client'
import { scrollWithin } from '../../lib/scroll-within'
import type { NotificationPrefsOut } from '../admin/api'
import { reads } from '../admin/api'
import { notificationPrefsQuery } from '../me/queries'
import { type CollapsibleTurn, defaultOpenIds, forcedOpenIds, type TurnCollapse } from './collapse'

export type TimelineOrder = 'newest' | 'oldest'

/** Below this, the people strip collapses to "N people". A ~1000px pane stays one row of chips (#616). */
export const PANE_TOOLBAR_PHONE_MAX_PX = 560

export function paneToolbarLayout(width: number): 'row' | 'phone' {
  return width > 0 && width < PANE_TOOLBAR_PHONE_MAX_PX ? 'phone' : 'row'
}

export const PaneToolbarLayoutContext = React.createContext<'row' | 'phone'>('row')

export const DEFAULT_TIMELINE_ORDER: TimelineOrder = 'newest'
export const TIMELINE_ORDER_KEY = 'patchbay:timeline-order'

export function isTimelineOrder(value: unknown): value is TimelineOrder {
  return value === 'newest' || value === 'oldest'
}

/** The browser copy, or the default. Never throws (private windows, blocked storage). */
export function storedTimelineOrder(): TimelineOrder {
  const v = safeStorage.getItem(TIMELINE_ORDER_KEY)
  return isTimelineOrder(v) ? v : DEFAULT_TIMELINE_ORDER
}

/**
 * Put turns in display order. Input is chronological (oldest first, as every API returns them); newest-first is its
 * reverse. Never mutates the input.
 */
export function orderTurns<T>(turns: readonly T[], order: TimelineOrder): T[] {
  return order === 'newest' ? [...turns].reverse() : [...turns]
}

export interface TimelineOrderState {
  order: TimelineOrder
  setOrder: (next: TimelineOrder) => void
  toggle: () => void
}

/**
 * The viewer's timeline order. `server: false` keeps it in this browser only (the contractor portal has no Patchbay
 * user to store it against). A failed save keeps the new order on this device and says so.
 */
export function useTimelineOrder({ server = true }: { server?: boolean } = {}): TimelineOrderState {
  const queryClient = useQueryClient()
  const prefs = useQuery({ ...notificationPrefsQuery(), enabled: server, retry: false, staleTime: 5 * 60_000 })
  const [local, setLocal] = React.useState<TimelineOrder>(storedTimelineOrder)
  const fromServer = server && isTimelineOrder(prefs.data?.timeline_order) ? prefs.data.timeline_order : null
  const order = fromServer ?? local

  // Keep the browser copy in step with the server so the next first paint is already right.
  React.useEffect(() => {
    if (fromServer) safeStorage.setItem(TIMELINE_ORDER_KEY, fromServer)
  }, [fromServer])

  const setOrder = React.useCallback(
    (next: TimelineOrder) => {
      setLocal(next)
      safeStorage.setItem(TIMELINE_ORDER_KEY, next)
      if (!server) return
      const key = notificationPrefsQuery().queryKey
      const before = queryClient.getQueryData<NotificationPrefsOut>(key)
      if (before) queryClient.setQueryData<NotificationPrefsOut>(key, { ...before, timeline_order: next })
      // An API that predates the field has nothing to store it in: the browser copy is the store.
      if (before && !isTimelineOrder(before.timeline_order)) return
      reads()
        .patchNotifications({ timeline_order: next }, currentCsrfToken() ?? '')
        .then((saved) => queryClient.setQueryData(key, saved))
        .catch(() => toast('Order saved on this device only', { description: 'Patchbay could not store it with your preferences.' }))
    },
    [queryClient, server],
  )
  const toggle = React.useCallback(() => setOrder(order === 'newest' ? 'oldest' : 'newest'), [order, setOrder])
  return { order, setOrder, toggle }
}

/** The newest first / oldest first switch that sits above a timeline. `iconOnly` is the pane toolbar (#578). */
export function TimelineOrderToggle({
  order,
  onToggle,
  className,
  iconOnly = false,
}: {
  order: TimelineOrder
  onToggle: () => void
  className?: string
  iconOnly?: boolean
}) {
  const newest = order === 'newest'
  const label = newest ? 'Newest first. Switch to oldest first' : 'Oldest first. Switch to newest first'
  const button = (
    <Button
      type="button"
      size={iconOnly ? 'icon-sm' : 'sm'}
      variant="ghost"
      className={cn(iconOnly ? 'text-muted-foreground' : 'text-xs text-muted-foreground', className)}
      onClick={onToggle}
      aria-pressed={newest}
      aria-label={label}
      title={iconOnly ? undefined : 'Timeline order (remembered for you)'}
      data-timeline-order={order}
    >
      {newest ? <ArrowDownWideNarrow className="size-3.5" /> : <ArrowUpNarrowWide className="size-3.5" />}
      {iconOnly ? null : newest ? 'Newest first' : 'Oldest first'}
    </Button>
  )
  if (!iconOnly) return button
  return <Tooltip content={label}>{button}</Tooltip>
}

/** "Jump to first message": brings the oldest turn into view, wherever the order put it. */
export function JumpToFirst({
  onJump,
  order,
  className,
  iconOnly = false,
}: {
  onJump: () => void
  order: TimelineOrder
  className?: string
  iconOnly?: boolean
}) {
  const label = 'Jump to first message'
  const button = (
    <Button
      type="button"
      size={iconOnly ? 'icon-sm' : 'sm'}
      variant={iconOnly ? 'ghost' : 'link'}
      className={cn(iconOnly ? 'text-muted-foreground' : 'h-auto px-0 text-xs', className)}
      aria-label={label}
      onClick={onJump}
    >
      {order === 'newest' ? <ChevronsDown className="size-3.5" /> : <ChevronsUp className="size-3.5" />}
      {iconOnly ? null : label}
    </Button>
  )
  if (!iconOnly) return button
  return <Tooltip content={label}>{button}</Tooltip>
}

function ToolbarIcon({ label, onClick, children }: { label: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <Tooltip content={label}>
      <Button type="button" size="icon-sm" variant="ghost" className="text-muted-foreground" aria-label={label} onClick={onClick}>
        {children}
      </Button>
    </Tooltip>
  )
}

/** Marks for the turn elements a timeline renders, so "open on newest" and "jump to first" can find them. */
export const TURN_EDGE_ATTR = 'data-turn-edge'
export function turnEdge(index: number, count: number, order: TimelineOrder): 'newest' | 'first' | undefined {
  if (count === 0) return undefined
  const newestAt = order === 'newest' ? 0 : count - 1
  const firstAt = order === 'newest' ? count - 1 : 0
  if (index === newestAt) return 'newest'
  if (index === firstAt) return 'first'
  return undefined
}

function edgeIn(root: ParentNode | null | undefined, edge: 'newest' | 'first'): HTMLElement | null {
  if (!root) return null
  // A one-turn timeline marks it "newest"; it is also the first message.
  return (
    root.querySelector<HTMLElement>(`[${TURN_EDGE_ATTR}="${edge}"]`) ??
    (edge === 'first' ? root.querySelector<HTMLElement>(`[${TURN_EDGE_ATTR}="newest"]`) : null)
  )
}

/** Scroll the oldest turn under `root` into view and move focus there (keyboard users land on it too). */
export function jumpToFirst(root: ParentNode | null | undefined): void {
  const el = edgeIn(root, 'first')
  if (!el) return
  scrollWithin(el, { block: 'start', behavior: 'smooth' })
  if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1')
  el.focus({ preventScroll: true })
}

/**
 * Open on the newest turn: once per `openKey` (a case + tab + order), bring the newest turn under `root` into view.
 * Newest-first puts it at the top already (no movement); oldest-first scrolls down to it. Later refreshes of the same
 * timeline never yank the scroll position.
 */
export function useOpenOnNewest(
  root: React.RefObject<HTMLElement | null> | (() => ParentNode | null | undefined),
  { openKey, order, ready = true }: { openKey: string; order: TimelineOrder; ready?: boolean },
): void {
  const done = React.useRef<string | null>(null)
  const key = `${openKey}:${order}`
  React.useEffect(() => {
    if (!ready || done.current === key) return
    const el = edgeIn(typeof root === 'function' ? root() : root.current, 'newest')
    if (!el) return
    done.current = key
    scrollWithin(el, { block: order === 'oldest' ? 'start' : 'nearest' })
  }, [root, key, order, ready])
}

export interface TimelineTurn {
  key: string
  /** The turn itself, for the collapse defaults (id, draft). */
  turn: CollapsibleTurn
  /** Draw the turn; `onToggleCollapsed` is absent for a turn that is always expanded (draft, being replied to). */
  render: (state: { collapsed: boolean; onToggleCollapsed?: () => void }) => React.ReactNode
}

export interface TimelineCollapse {
  state: TurnCollapse
  /** How many of the newest turns open expanded (the person's preference). */
  count: number
  replyingTo?: string | null
}

/** Which turns are collapsible, and which of those are open now. */
export function collapseView(turns: readonly CollapsibleTurn[], collapse: TimelineCollapse) {
  const defaults = defaultOpenIds(turns, collapse.count)
  const forced = forcedOpenIds(turns, collapse.replyingTo)
  const collapsible = turns.filter((t) => !forced.has(t.id)).map((t) => t.id)
  const open = (id: string) => forced.has(id) || collapse.state.isOpen(id, defaults)
  return { defaults, forced, collapsible, open }
}

/** The shortcut: expand everything if anything is collapsed, otherwise collapse everything that can be. */
export function toggleAllTurns(turns: readonly CollapsibleTurn[], collapse: TimelineCollapse): void {
  const v = collapseView(turns, collapse)
  collapse.state.setAll(
    v.collapsible,
    v.collapsible.some((id) => !v.open(id)),
  )
}

/**
 * A pane's timeline in the viewer's order. `turns` arrive chronological. Newest first puts the composer at the top,
 * directly above the newest turn (its quoted context sits right below it); oldest first keeps the composer under the
 * last turn. `after` (e.g. turns not classified yet) stays below the turns either way. With `collapse` (#220) the
 * older turns fold to one line and the toolbar has Expand all / Collapse all.
 *
 * Every slot is always rendered (empty when unused), so the composer keeps the same place among its siblings and
 * flipping the order mid-reply never remounts the editor or loses what was typed. DOM order is the visual order,
 * so reading and Tab order follow what is on screen.
 */
export function OrderedTimeline({
  order,
  turns,
  composer,
  after,
  onToggle,
  collapse,
  leading,
  trailing,
}: {
  order: TimelineOrder
  turns: TimelineTurn[]
  composer: React.ReactNode
  after?: React.ReactNode
  onToggle: () => void
  collapse?: TimelineCollapse
  /** Pane participants and the party's visit chip, on the left of the one toolbar (#578, #616). */
  leading?: React.ReactNode
  /** The dismissible time chip, on the right of that toolbar. */
  trailing?: React.ReactNode
}) {
  const ref = React.useRef<HTMLDivElement>(null)
  const toolbarRef = React.useRef<HTMLDivElement>(null)
  const [toolbarLayout, setToolbarLayout] = React.useState<'row' | 'phone'>('row')
  const newest = order === 'newest'
  const view = collapse
    ? collapseView(
        turns.map((t) => t.turn),
        collapse,
      )
    : null
  const shown = orderTurns(turns, order)
  const list = shown.map((t, i) => {
    const id = t.turn.id
    const collapsible = Boolean(view && collapse && !view.forced.has(id))
    const isOpen = !view || view.open(id)
    return (
      <div key={t.key} {...{ [TURN_EDGE_ATTR]: turnEdge(i, shown.length, order) }}>
        {t.render({
          collapsed: !isOpen,
          onToggleCollapsed: collapsible && view && collapse ? () => collapse.state.toggle(id, view.defaults) : undefined,
        })}
      </div>
    )
  })
  const slot = (content: React.ReactNode) => (content ? <div className="space-y-3">{content}</div> : null)
  const canFold = Boolean(view && view.collapsible.length > 0)
  const showToolbar = turns.length > 0 || Boolean(leading) || Boolean(trailing)
  React.useEffect(() => {
    if (!showToolbar) return
    const el = toolbarRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const apply = (width: number) => setToolbarLayout(paneToolbarLayout(width))
    apply(el.getBoundingClientRect().width)
    const observer = new ResizeObserver((entries) => apply(entries[0]?.contentRect.width ?? 0))
    observer.observe(el)
    return () => observer.disconnect()
  }, [showToolbar])
  const actions =
    turns.length > 0 ? (
      <>
        <TimelineOrderToggle order={order} onToggle={onToggle} iconOnly />
        {canFold && view && collapse ? (
          <>
            <ToolbarIcon label="Expand all" onClick={() => collapse.state.setAll(view.collapsible, true)}>
              <ChevronsUpDown className="size-3.5" />
            </ToolbarIcon>
            <ToolbarIcon label="Collapse all" onClick={() => collapse.state.setAll(view.collapsible, false)}>
              <ChevronsDownUp className="size-3.5" />
            </ToolbarIcon>
          </>
        ) : null}
        {turns.length > 1 ? <JumpToFirst order={order} onJump={() => openAndJump()} iconOnly /> : null}
      </>
    ) : null
  return (
    <div ref={ref} className="space-y-3" data-timeline-order={order}>
      {showToolbar ? (
        <TooltipProvider>
          {/* One row. Chips shrink; on a phone-width pane they collapse to a count (#616). */}
          <PaneToolbarLayoutContext.Provider value={toolbarLayout}>
            <div
              ref={toolbarRef}
              data-pane-toolbar
              data-timeline-toolbar
              data-pane-toolbar-layout={toolbarLayout}
              role="toolbar"
              aria-label="Conversation"
              className="flex flex-nowrap items-center gap-x-2 gap-y-1"
            >
              {leading ? (
                <div data-pane-toolbar-leading className="flex min-w-0 max-w-full flex-1 flex-nowrap items-center gap-1.5 overflow-hidden">
                  {leading}
                </div>
              ) : null}
              {actions || trailing ? (
                <div data-pane-toolbar-actions className="ml-auto flex shrink-0 flex-nowrap items-center gap-0.5">
                  {actions}
                  {trailing}
                </div>
              ) : null}
            </div>
          </PaneToolbarLayoutContext.Provider>
        </TooltipProvider>
      ) : null}
      {slot(newest ? null : list)}
      {slot(newest ? null : after)}
      <div className="space-y-3" data-composer-slot={newest ? 'top' : 'bottom'}>
        {composer}
      </div>
      {slot(newest ? list : null)}
      {slot(newest ? after : null)}
    </div>
  )

  // Jumping to the first message also opens it, so the jump lands on something readable.
  function openAndJump() {
    const first = turns[0]?.turn.id
    if (first && view && collapse && !view.open(first)) collapse.state.setAll([first], true)
    requestAnimationFrame(() => jumpToFirst(ref.current))
  }
}

export {
  type CollapsibleTurn,
  DEFAULT_EXPANDED_TURNS,
  defaultOpenIds,
  forcedOpenIds,
  type TurnCollapse,
  useExpandedTurnCount,
  useExpandedTurnPreference,
  useTurnCollapse,
} from './collapse'
